import { MultiSelectMenuHandle, MultiSelectMenuProps } from "./types";
import {
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { warnOnce } from "../../../internal/functions";
import { CheckState } from "../../../types";
import { MultiSelectOptions } from "../../../internal/components/MultiSelectOptions";
import { useDebouncedCallback } from "../../../internal/hooks/useDebouncedCallback";
import { OptionsPopover } from "../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../internal/components/OptionsDialog/OptionsDialog";
import { useProcessedOptions } from "../../../internal/hooks/useProcessedOptions";
import { buildSelectItems } from "../../../internal/functions/buildSelectItems";
import { useMenuInteraction } from "../SelectMenu/internal/useMenuInteraction";
import { useToggleSelection } from "../../../internal/hooks/useToggleSelection";
import { SelectItem } from "../../../internal/types/selectFieldInternalTypes";
import { useSelectOrchestration } from "../../../internal/hooks/useSelectOrchestration";
import { SearchField } from "../../../components";

/**
 * MultiSelectMenu component for selecting multiple options from a dropdown triggered by any element.
 *
 * Features:
 * - Attaches to any trigger element via a render prop
 * - Multiple selection with checkbox indicators
 * - Optional search input rendered inside the popover/dialog
 * - Lazy loading with pagination (page, offset, or group modes)
 * - Optional "Select All" functionality
 * - Optional "Select Filtered" functionality
 * - Pinned options support
 * - Grouped options support
 * - Supports both popover and dialog display modes
 * - Adaptive display based on device (mobile uses dialog)
 * - Cache support for loadOptions results
 * - Full accessibility support with ARIA attributes
 * - Controlled selection state
 * - Configurable popover width
 *
 * @example
 * <MultiSelectMenu
 *   trigger={(props) => <Button {...props}>Select books</Button>}
 *   label="Books"
 *   value={selectedOptions}
 *   onSelectedOptionsChange={setSelectedOptions}
 *   loadOptions={loadOptions}
 * />
 */
export const MultiSelectMenu = forwardRef<
  MultiSelectMenuHandle,
  MultiSelectMenuProps
>((props, ref) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const {
    id: idProp,
    value: selectedOptions,
    initialLoad: initialLoadProp,
    displayMenuAs: displayMenuAsProp,
    lazy,
    loadOptions: loadOptionsProp,
    label,
    searchPlaceholder,
    debounceMs = 200,
    onSelectedOptionsChange,
    cache,
    pinned,
    selectAll: selectAllProp,
    selectFiltered: selectFilteredProp,
    virtualize,
    disableSearch,
    popoverWidth,
    trigger,
    onSearchChange,
    onMenuKeyDown,
    onImplicitClose,
    onExplicitClose,
  } = props;

  const groupToString =
    "groupToString" in props ? props.groupToString : undefined;
  const groupSorter = "groupSorter" in props ? props.groupSorter : undefined;

  const autoId = useId();
  const id = idProp ?? autoId;

  if (disableSearch && selectFilteredProp) {
    warnOnce(
      "MultiSelectMenu: `selectFiltered` has no effect when `disableSearch` is true because there is no search input to produce filtered results.",
    );
  }

  const {
    displayAs,
    initialLoad,
    initialLoadPerformed,
    setInitialLoadPerformed,
    options,
    loading,
    loadingMore,
    hasMore,
    loadOptions,
    loadMore,
  } = useSelectOrchestration({
    lazy,
    loadOptions: loadOptionsProp,
    cache,
    lazyOptions: "lazyOptions" in props ? props.lazyOptions : undefined,
    initialLoad: initialLoadProp,
    displayMenuAs: displayMenuAsProp,
    ref,
  });

  const selectAll = useMemo(
    () =>
      selectAllProp
        ? {
            label: selectAllProp.label ?? "Select All",
            onClick: selectAllProp.onClick,
            checkState: (selectAllProp.checkState === true
              ? "checked"
              : selectAllProp.checkState === false
                ? "unchecked"
                : (selectAllProp.checkState ?? "indeterminate")) as CheckState,
          }
        : undefined,
    [selectAllProp],
  );

  const selectFiltered = useMemo(
    () =>
      selectFilteredProp
        ? (searchValue: string) => {
            const config = selectFilteredProp(searchValue);
            return {
              label: config.label,
              onClick: config.onClick,
              checkState: (config.checkState === true
                ? "checked"
                : config.checkState === false
                  ? "unchecked"
                  : (config.checkState ?? "indeterminate")) as CheckState,
            };
          }
        : undefined,
    [selectFilteredProp],
  );

  const [inputValue, setInputValue] = useState("");

  const searchValue = disableSearch ? "" : inputValue;

  const { pinnedSections, ungroupedItems, groupSections } = useProcessedOptions(
    {
      options,
      pinned,
      searchValue,
      groupToString,
      groupSorter,
    },
  );

  const { handleItemClick } = useToggleSelection({
    options,
    pinnedSections,
    selectedOptions,
    onSelectedOptionsChange,
  });

  const bulkActionItems = useMemo(() => {
    const items: SelectItem[] = [];
    if (selectAll && !searchValue) {
      items.push({
        id: "select-all",
        type: "select-all",
        original: { id: "select-all", label: selectAll.label ?? "Select All" },
        checkState: selectAll.checkState,
      });
    }
    if (selectFiltered && searchValue && !loading) {
      const config = selectFiltered(searchValue);
      items.push({
        id: "select-filtered",
        type: "select-filtered",
        original: {
          id: "select-filtered",
          label: config.label ?? "Select Filtered",
        },
        checkState: config.checkState,
      });
    }
    return items;
  }, [selectAll, selectFiltered, searchValue, loading]);

  const { items, sectionsMeta } = useMemo(
    () =>
      buildSelectItems({
        prefixItems: bulkActionItems,
        pinnedSections,
        groupSections,
        ungroupedItems,
      }),
    [bulkActionItems, pinnedSections, groupSections, ungroupedItems],
  );

  const selectedItemIds = useMemo(
    () => new Set(selectedOptions.map((o) => o.id)),
    [selectedOptions],
  );

  const handleItemActivate = useCallback(
    (item: SelectItem) => {
      if (item.type === "select-all") {
        selectAll?.onClick();
      } else if (item.type === "select-filtered" && selectFiltered) {
        const config = selectFiltered(searchValue);
        config.onClick();
      } else if (
        item.type === "option" ||
        item.type === "pinned-option" ||
        item.type === "grouped-option"
      ) {
        handleItemClick(item.original);
      }
    },
    [selectAll, selectFiltered, searchValue, handleItemClick],
  );

  const {
    isOpen,
    closeMenu,
    highlightedIndex,
    getTriggerProps,
    getMenuProps,
    getItemProps,
    listRef,
    menuContainerRef,
    handleKeyDown,
  } = useMenuInteraction({
    items: items,
    sectionsMeta,
    onItemActivate: handleItemActivate,
    selectedItemIds,
    displayAs,
    closeOnActivate: false,
    id,
    triggerRef,
    label,
    onImplicitClose,
    onExplicitClose,
  });

  const debouncedOptionLoader = useDebouncedCallback((value: string) => {
    loadOptions(value, { initial: true });
  }, debounceMs);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      debouncedOptionLoader(value);
      onSearchChange?.(value);
    },
    [debouncedOptionLoader, onSearchChange],
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      onMenuKeyDown?.(e);
      handleKeyDown(e);
    },
    [handleKeyDown, onMenuKeyDown],
  );

  const getMenuPropsWithKeyDown: typeof getMenuProps = useCallback(
    (opts) => {
      const menuProps = getMenuProps(opts);
      if (!onMenuKeyDown) return menuProps;
      return {
        ...menuProps,
        onKeyDown: (e: KeyboardEvent) => {
          onMenuKeyDown(e);
          menuProps.onKeyDown(e);
        },
      };
    },
    [getMenuProps, onMenuKeyDown],
  );

  useEffect(() => {
    const performInitialLoad = async () => {
      if (initialLoad === "immediate" && !initialLoadPerformed) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    };
    performInitialLoad();
  }, [loadOptions, initialLoadPerformed, initialLoad, setInitialLoadPerformed]);

  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      if (initialLoad === "open" && !initialLoadPerformed) {
        loadOptions("", { initial: true }).then(() => {
          setInitialLoadPerformed(true);
        });
      }
    }

    if (!isOpen && wasOpen) {
      setInputValue("");
      loadOptions("", { initial: true });
    }
  }, [
    isOpen,
    initialLoad,
    initialLoadPerformed,
    loadOptions,
    setInitialLoadPerformed,
  ]);

  useEffect(() => {
    if (!isOpen || displayAs !== "popover") return;

    const frameId = requestAnimationFrame(() => {
      if (!disableSearch) {
        searchRef.current?.focus();
      } else {
        menuContainerRef.current?.focus();
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [isOpen, displayAs, disableSearch, menuContainerRef]);

  const handleLoadMore = async () => {
    await loadMore(searchValue);
  };

  const handleClickOutside = useCallback(() => {
    closeMenu({ restoreFocus: false });
  }, [closeMenu]);

  const triggerProps = getTriggerProps();

  const menuPropsForAria = getMenuProps();
  const activeDescendant = menuPropsForAria["aria-activedescendant"];

  const handleSearchClear = useCallback(() => {
    setInputValue("");
    debouncedOptionLoader("");
    onSearchChange?.("");
  }, [debouncedOptionLoader, onSearchChange]);

  const searchInput = !disableSearch ? (
    <SearchField
      ref={searchRef}
      value={inputValue}
      onChange={handleInputChange as React.ChangeEventHandler<HTMLInputElement>}
      onKeyDown={
        handleInputKeyDown as React.KeyboardEventHandler<HTMLInputElement>
      }
      onClear={handleSearchClear}
      aria-activedescendant={activeDescendant}
      aria-controls={menuPropsForAria.id}
      placeholder={searchPlaceholder}
      type="search"
    />
  ) : null;

  if (displayAs === "dialog") {
    return (
      <>
        {trigger(triggerProps)}
        <OptionsDialog
          id={`${id}-dialog`}
          isOpen={isOpen}
          onClose={closeMenu}
          title={label}
          field={searchInput ?? undefined}
          initialFocusResolver={
            !disableSearch
              ? (elements) =>
                  elements.find((el) => el.tagName === "INPUT") || elements[0]
              : undefined
          }
        >
          <MultiSelectOptions
            ref={listRef}
            isOpen
            items={items}
            sectionsMeta={sectionsMeta}
            getMenuProps={(opts) => {
              const menuProps = getMenuPropsWithKeyDown(opts);
              if (disableSearch) {
                return menuProps;
              }
              return { ...menuProps, tabIndex: -1 };
            }}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOptions={selectedOptions}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            virtualize={virtualize}
          />
        </OptionsDialog>
      </>
    );
  }

  return (
    <>
      {trigger(triggerProps)}
      <OptionsPopover
        id={`${id}-popover`}
        referenceElement={triggerRef}
        open={isOpen && displayAs === "popover"}
        onClickOutside={handleClickOutside}
        width={popoverWidth ?? 320}
      >
        {searchInput}
        <MultiSelectOptions
          ref={listRef}
          isOpen={isOpen}
          items={items}
          sectionsMeta={sectionsMeta}
          getMenuProps={(opts) => {
            const menuProps = getMenuPropsWithKeyDown(opts);
            if (disableSearch) {
              return menuProps;
            }
            return { ...menuProps, tabIndex: -1 };
          }}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          selectedOptions={selectedOptions}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          loading={loading}
          loadingMore={loadingMore}
          virtualize={virtualize}
        />
      </OptionsPopover>
    </>
  );
});

MultiSelectMenu.displayName = "MultiSelectMenu";
